import { Badge, Col, Divider, Empty, Flex, Modal, Space } from "antd";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import StatusTag from "components/elements/TagCustom/StatusTag";
import { useEffect, useState } from "react";

const EmployeeWorkinDetailModal = ({
    isLeaveDetailModalOpen = false,
    setIsLeaveDetailModalOpen = () => { },
    selectedDate = "",
    selectedEmployee = "",
    employeeName = null,
    requests = []
}) => {
    const { t, i18n } = useTranslation();
    const [selectedRequest, setSelectedRequest] = useState(null);

    useEffect(() => {
        if (isLeaveDetailModalOpen && requests?.length > 0) {
            setSelectedRequest(requests[0]);
        } else {
            setSelectedRequest(null);
        }
    }, [isLeaveDetailModalOpen, requests]);

    const getBadgeStyle = (req) => {
        let badgeColor = "#667085";
        let backgroundColor = "#EAEBEC";

        if (req?.type === "overtime") {
            badgeColor = "#9333EA";
            backgroundColor = "#F3E8FF";
        } else if (req?.type === "offsite") {
            badgeColor = "#16A34A";
            backgroundColor = "#DCFCE7";
        } else if (req?.type === "leave") {
            badgeColor = "#EC4899";
            backgroundColor = "#FCE7F3";
        } else if (
            req?.type === "overtimePending" ||
            req?.type === "leavePending" ||
            req?.type === "offsitePending"
        ) {
            badgeColor = "#FFBF1A";
            backgroundColor = "#FFF8E0";
        }

        return { badgeColor, backgroundColor };
    };

    const renderRequestBadgeItem = (req) => {
        const isActive = selectedRequest && String(selectedRequest?.id) === String(req?.id);
        const badgeText = req?.title || "-";
        const { badgeColor, backgroundColor } = getBadgeStyle(req);

        return (
            <div
                key={String(req?.id)}
                onClick={() => setSelectedRequest(req)}
                style={{
                    cursor: "pointer",
                    marginBottom: "8px",
                    backgroundColor: isActive ? "#EEF2FF" : "#F8FAFC",
                    border: isActive ? "1px solid var(--button-color)" : "1px solid #ffff",
                    transition: "all 0.15s ease-in-out",
                    borderRadius: "6px"
                }}
            >
                <Badge
                    color={badgeColor}
                    text={badgeText}
                    style={{ backgroundColor: backgroundColor, borderRadius: "5px", padding: "2px 8px", color: badgeColor, width: "100%" }}
                />
            </div>
        );
    };

    const getDurationText = (doc, t) => {
        if (!doc) return "";

        let duration = "";

        if (doc?.overtimeShiftDetails && Array.isArray(doc?.overtimeShiftDetails)) {
            const totalMinutes = doc?.overtimeShiftDetails
                ?.reduce((sum, detail) => sum + (detail.totalCal || 0), 0);

            const hours = Math.floor(totalMinutes / 60);
            const minutes = totalMinutes % 60;

            if (hours > 0 && minutes > 0) {
                duration = `${hours} ${t("unit.hours")} ${minutes} ${t("unit.minute")}`;
            } else if (hours > 0) {
                duration = `${hours} ${t("unit.hour")}`;
            } else if (minutes > 0) {
                duration = `${minutes} ${t("unit.minute")}`;
            }
        }
        else if (doc?.durationType === "ALLDAY") {
            duration = `${doc?.totalDuration} ${t("unit.day")}`;
        }
        else if (doc?.durationType === "HOUR") {
            const totalMinutes = doc?.totalDuration || 0;
            const hours = Math.floor(totalMinutes / 60);
            const minutes = totalMinutes % 60;

            if (hours > 0 && minutes > 0) {
                duration = `${hours} ${t("unit.hours")} ${minutes} ${t("unit.minute")}`;
            } else if (hours > 0) {
                duration = `${hours} ${t("unit.hour")}`;
            } else if (minutes > 0) {
                duration = `${minutes} ${t("unit.minute")}`;
            }
        }
        return duration;
    };

    const getOtCodeNameText = (doc, i18n) => {
        if (!doc || !Array.isArray(doc.overtimeShiftDetails)) return "";

        return doc?.overtimeShiftDetails
            .map(detail =>
                i18n.language === "en" ? detail?.otCodeNameEn : detail?.otCodeName
            )
            .filter(Boolean)
            .join(", ");
    };

    const renderDetailSection = () => {
        if (!selectedRequest) {
            return (
                <Flex justify="center" align="center" style={{ height: "100%" }}>
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                </Flex>
            );
        }

        const { badgeColor, backgroundColor } = getBadgeStyle(selectedRequest);
        const durationText = getDurationText(selectedRequest?.source, t, i18n);
        const eventDate = selectedRequest?.date ? dayjs(selectedRequest?.date).format("DD/MM/YYYY") : "-";

        let badgeText = selectedRequest?.title || "-";

        if (selectedRequest?.rawType === "otDocument") {
            const otCodeText = getOtCodeNameText(selectedRequest?.source, i18n);
            if (otCodeText) {
                badgeText = otCodeText;
            }
        }
        return (
            <Space direction="vertical" style={{ width: "100%" }} size={8}>
                <span style={{ color: "#344054", fontSize: "18px" }}>
                    {selectedEmployee?.employeeName || employeeName || '-'}
                </span>

                <Badge
                    color={badgeColor}
                    text={badgeText}
                    style={{ backgroundColor: backgroundColor, borderRadius: "5px", padding: "2px 8px", color: badgeColor, width: "100%" }}
                />

                <Flex justify="space-between">
                    <span style={{ color: "#667085", borderLeft: "2px solid #757575", paddingLeft: "8px" }}>
                        {eventDate}
                    </span>
                    <span>{durationText}</span>
                </Flex>

                <Divider style={{ border: "0.5px solid #ddd", margin: "8px 0px" }} />

                <Flex justify="space-between" align="end">
                    <Flex vertical>
                        <span style={{ fontSize: "12px", color: "#A0AEC0" }}>
                            {t("expense.expense-detail.request-id")}
                        </span>
                        <span>{selectedRequest?.source?.sequence || "-"}</span>
                    </Flex>

                    <div style={{ height: "max-content" }}>
                        <StatusTag value={selectedRequest?.requestStatus} />
                    </div>
                </Flex>
            </Space>
        );
    };

    return (
        <Modal
            title={<span style={{ color: "#344054", fontWeight: "bold", fontSize: "18px" }}>{
                i18n.language == "en" ? dayjs(selectedDate).format("DD/MM/YYYY")
                    : dayjs(selectedDate).format("DD/MM/BBBB")}
            </span>}
            open={isLeaveDetailModalOpen}
            onCancel={() => setIsLeaveDetailModalOpen(false)}
            footer={null}
            width={"40vw"}
        >
            <Flex style={{ width: "100%" }} justify="space-between">
                <Col span={10} style={{ maxHeight: "60vh", overflowY: "auto" }}>
                    {requests?.length
                        ? requests?.map(renderRequestBadgeItem)
                        : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                    }
                </Col>
                <Divider type="vertical" style={{ border: "0.5px solid #ddd", height: "auto" }} />
                <Col span={13}>
                    {renderDetailSection()}
                </Col>
            </Flex>
        </Modal>
    )
}

export default EmployeeWorkinDetailModal;